import {
  createRootRoute,
  createRouter,
  Outlet,
  retainSearchParams,
  type AnyRoute,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import type { ComponentType, ReactNode } from "react";

import { EmbedMeta } from "./embed-meta";
import { NotFoundPage } from "./not-found-page";
import { monitorQueryParameters } from "./query-param-security";
import { RouterErrorComponent } from "./router-error-component";
import { WIDGET_SCROLL_RESTORATION_SELECTOR } from "./scroll-restoration";
import { RETAINED_SEARCH_PARAMS, rootSearchSchema } from "./search-params-schema";

export function isFullyMatchedPath(
  pathname: string,
  matches: Array<{ pathname: string }>,
): boolean {
  return matches.at(-1)?.pathname === pathname;
}

export function createWidgetRouter<TRouteTree extends AnyRoute>(routeTree: TRouteTree) {
  // @ts-expect-error RouterContextOptions<TRouteTree> is a deferred conditional that TS
  // cannot resolve in a generic body; our widget route trees define no router context.
  return createRouter({
    routeTree,
    defaultPreload: "intent",
    defaultStaleTime: 5000,
    scrollRestoration: false,
    scrollToTopSelectors: ["window", WIDGET_SCROLL_RESTORATION_SELECTOR],
    defaultNotFoundComponent: () => <NotFoundPage />,
  });
}

/**
 * Creates the root route with common scaffolding (search-param retention,
 * query-param security monitoring, embed meta, error/notfound pages).
 *
 * Each widget passes its own wrappers for showcase vs standalone mode —
 * these are the only product-specific parts of the root.
 */
export function createWidgetRootRoute({
  ShowcaseWrapper,
  StandaloneWrapper,
}: {
  ShowcaseWrapper: ComponentType<{ children: ReactNode }>;
  StandaloneWrapper: ComponentType<{ children: ReactNode }>;
}) {
  const isShowcase = import.meta.env.VITE_WIDGET_SHOWCASE === "true";

  function RootNotFoundPage() {
    return <NotFoundPage fullHeight />;
  }

  function RootComponent() {
    const Wrapper = isShowcase ? ShowcaseWrapper : StandaloneWrapper;
    return (
      <>
        <EmbedMeta />
        <Wrapper>
          <Outlet />
        </Wrapper>
      </>
    );
  }

  return createRootRoute({
    validateSearch: zodValidator(rootSearchSchema),
    search: {
      middlewares: [retainSearchParams([...RETAINED_SEARCH_PARAMS])],
    },
    component: RootComponent,
    notFoundComponent: RootNotFoundPage,
    errorComponent: ({ error, reset }) => <RouterErrorComponent error={error} reset={reset} />,
    beforeLoad: () => {
      monitorQueryParameters();
    },
  });
}
